/**
 * GridList — built on React Aria Components.
 *
 * Accessible list with keyboard navigation, selection support,
 * and interactive children (buttons, menus inside items).
 *
 * Styled as an editorial journal list: items sit directly on the linen
 * background, separated by hairline rules. No card chrome — typography
 * and whitespace do the work.
 *
 * Usage:
 *   <AriaGridList aria-label="Sources" onAction={(key) => navigate(`/sources/${key}`)}>
 *     <AriaGridListItem id="1" textValue="ISO 45001">
 *       <div>...</div>
 *     </AriaGridListItem>
 *   </AriaGridList>
 */

import {
  GridList,
  GridListItem,
  Button as AriaButton,
  type GridListProps,
  type GridListItemProps,
} from "react-aria-components";
import { cn } from "@/lib/utils";
import { AriaCheckbox } from "./aria-checkbox";

function AriaGridList<T extends object>({
  className,
  ...props
}: GridListProps<T>) {
  return (
    <GridList
      className={cn(
        "flex flex-col divide-y divide-border/60 border-y border-border/60",
        className,
      )}
      {...props}
    />
  );
}

function AriaGridListItem({
  className,
  children,
  ...props
}: GridListItemProps) {
  return (
    <GridListItem
      className={({ isHovered, isSelected, isFocusVisible }) =>
        cn(
          "flex items-center px-3 py-3 outline-none cursor-pointer transition-colors",
          "[&_[role=gridcell]>:not(label)]:min-w-0 [&_[role=gridcell]>:not(label)]:flex-1",
          isHovered && "bg-muted/40",
          isSelected && "bg-primary/[0.04]",
          isFocusVisible && "ring-2 ring-ring ring-inset",
          className,
        )
      }
      {...props}
    >
      {children}
    </GridListItem>
  );
}

/**
 * Selection checkbox for grid list items.
 * Larger hit target (44px) than standalone checkbox for easy clicking in rows.
 */
function AriaGridListCheckbox({
  "aria-label": ariaLabel = "Select row",
}: {
  "aria-label"?: string;
} = {}) {
  return (
    <AriaCheckbox
      slot="selection"
      aria-label={ariaLabel}
      className="p-[13px] -m-[13px] self-center"
    />
  );
}

/**
 * Action button inside a grid list item.
 * Uses React Aria's Button so it doesn't interfere with row selection/navigation.
 */
function AriaGridListAction({
  className,
  ...props
}: React.ComponentProps<typeof AriaButton>) {
  return (
    <AriaButton
      className={cn(
        "flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors",
        "hover:bg-muted hover:text-foreground",
        "focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
      {...props}
    />
  );
}

export {
  AriaGridList,
  AriaGridListItem,
  AriaGridListCheckbox,
  AriaGridListAction,
};
